import Button from "@/components/Button"
import Modal from "@/components/modals/Modal"
import { validateTextSettings, textToEqSettings, isRoomEQFormat } from "@/utils/equaliser"
import { readFileAsync } from "@/utils/file"
import { ModalBody, ModalContent, ModalFooter } from "@nextui-org/react"
import cx from "classnames"
import { fromEvent } from "file-selector"
import { useEffect, useState } from "react"
import { DropEvent, useDropzone } from "react-dropzone"

import { ReactComponent as CheckIcon } from "@/icons/check-icon.svg"
import { ReactComponent as CloudIcon } from "@/icons/cloud-icon.svg"

const ImportPresetModal = ({
  isOpen,
  onClose,
  onOpenChange,
  onImportPresetConfirmed,
  deviceHas12Bands = true, // Default to 12-band support
  refreshDeviceProfiles,
  ...props
}) => {
  const {
    getRootProps,
    getInputProps,
    open,
    isDragAccept,
    isDragReject,
    acceptedFiles,
    fileRejections,
    isDragActive,
  } = useDropzone({
    accept: {
      "text/plain": [".txt"],
    },
    maxFiles: 1,
    noClick: true,
    noKeyboard: true,
    // react-dropzone doesn't support async validators so we need to use this
    // function to pre-read the content of the text file so that it's available
    // when it's passed to the validator. Slightly hacky, see for more:
    // https://github.com/react-dropzone/react-dropzone/issues/1161
    getFilesFromEvent: async (event: DragEvent | DropEvent | FileSystemFileHandle[]) => {
      const files = await fromEvent(event)
      return Promise.all(
        Array.from(files)
          .filter((file) => file instanceof File)
          .map(async (file) => {
            if (file.type === "text/plain") {
              Object.defineProperty(file, "content", {
                value: await readFileAsync(file),
              })
            }
            return file
          })
      )
    },
    // Validate the content of the uploaded text file to make sure it conforms
    // the format required to convert to our JSON settings.
    validator: (file) => {
      if (!file.content) {
        return {
          code: "no-content",
          message: "Couldn't read file content",
        }
      }

      const errors = validateTextSettings(file.content, deviceHas12Bands)
      if (errors.length > 0) {
        console.log("Validation errors in imported file:")
        errors.forEach((error) => console.log(`- ${error}`))
        return errors.map((error) => {
          return {
            code: "validation-error",
            message: error,
          }
        })
      }

      return null
    },
  })

  /* Handle modal state */

  const [acceptedFile, setAcceptedFile] = useState()
  const [errors, setErrors] = useState<string[]>([])
  const [parsedSettings, setParsedSettings] = useState(null)

  useEffect(() => {
    if (acceptedFiles.length > 0) {
      setAcceptedFile(acceptedFiles[0])
      setErrors([])

      // Parse the file content for preview if validation passed
      const file = acceptedFiles[0] as any
      if (file && file.content) {
        try {
          const settings = textToEqSettings(file.content, deviceHas12Bands)
          // Store original content for format detection in preview
          settings.originalContent = file.content
          setParsedSettings(settings)
        } catch (error) {
          console.warn("Failed to parse file for preview:", error)
          setParsedSettings(null)
        }
      }
    }
  }, [acceptedFiles, deviceHas12Bands])

  useEffect(() => {
    if (fileRejections.length > 0) {
      setAcceptedFile(null)
      setErrors(fileRejections[0].errors)
      setParsedSettings(null)
    }
  }, [fileRejections])

  const reset = () => {
    setAcceptedFile(null)
    setErrors([])
    setParsedSettings(null)
  }

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      reset()
    }
  }, [isOpen])

  // Get the maximum filter count based on device capabilities
  const maxFilters = deviceHas12Bands ? 12 : 10

  return (
    <Modal isOpen={isOpen} onClose={onClose || onOpenChange} {...props}>
      <ModalContent className="max-w-4xl">
        {(onModalClose) => (
          <>
            <ModalBody className="pb-0">
              <div className="type-body-xl font-bold text-white">
                Import from AutoEQ, Squiglink, Room EQ Wizard, etc.
              </div>
              <div className="type-body-sm mt-4 text-ghost">
                {deviceHas12Bands ? (
                  <>
                    You may import up to {maxFilters} bands of any type (lowshelf, highshelf, or
                    peaking). Please see{" "}
                    <a
                      href="/example-import.txt"
                      download
                      className="text-red transition-colors hover:text-dark-red"
                    >
                      example-import.txt
                    </a>{" "}
                    for expected format.
                  </>
                ) : (
                  <>
                    You may import up to {maxFilters} bands, including up to 8 Peaking filters, 1
                    Lowshelf filter, and 1 Highshelf filter. Please see{" "}
                    <a
                      href="/example-import.txt"
                      download
                      className="text-red transition-colors hover:text-dark-red"
                    >
                      example-import.txt
                    </a>{" "}
                    for expected format.
                  </>
                )}
                <br />
                <i>
                  AutoEQ.app Tip: "EqualizerAPO ParametricEq" exports can be directly imported to
                  Element IV.
                </i>
              </div>
              <div className="">
                <div
                  className={cx(
                    "mt-4 flex min-h-[200px] flex-col items-center justify-center rounded-plus border p-8 transition-colors",
                    {
                      "border-red bg-brown text-red": isDragReject && errors.length > 0,
                      "border-dashed border-silver bg-dark bg-opacity-20": isDragAccept,
                      "border-mine-shaft": !isDragActive,
                      "border-silver bg-mine-shaft": isDragActive,
                    }
                  )}
                  {...getRootProps()}
                >
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <div className="type-body-lg font-medium text-silver">Drop the file here</div>
                  ) : (
                    <>
                      <CloudIcon
                        className={cx(
                          "mb-5 h-10 w-10",
                          isDragReject && errors.length > 0 ? "text-darker-red" : "text-tundora"
                        )}
                      />
                      <div className="flex max-w-md flex-col gap-y-4 text-center">
                        <div
                          className={cx(
                            "type-body-lg font-medium",
                            isDragReject && errors.length > 0 ? "text-darker-red" : "text-tundora"
                          )}
                        >
                          Drop your file here, or select the file from your computer
                        </div>
                        <div>
                          <Button theme="blue" onPress={open} size="sm">
                            Select file
                          </Button>
                        </div>
                        {acceptedFile && (
                          <div className="type-body-sm mt-2 inline-flex items-center self-center rounded-lg border border-dark-green bg-green bg-opacity-20 px-4 py-2 font-bold text-white">
                            {(acceptedFile as any).name} ({(acceptedFile as any).size} bytes){" "}
                            <CheckIcon className="ml-3 h-4 w-4 text-green" />
                          </div>
                        )}
                        {errors.length > 0 && (
                          <div className="flex flex-col gap-y-2">
                            {errors.map((error, i) => {
                              return (
                                <div key={`error-${i}`} className="type-body-sm font-bold text-red">
                                  {(error as any).message}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Show preview when file is successfully parsed */}
              {parsedSettings && (
                <EQPreview settings={parsedSettings} deviceHas12Bands={deviceHas12Bands} />
              )}
            </ModalBody>
            <ModalFooter className="gap-x-0">
              {acceptedFile && (
                <>
                  <Button
                    theme="blue"
                    size="sm"
                    onPress={async () => {
                      try {
                        await onImportPresetConfirmed(acceptedFile)
                        // Refresh device profiles after successful import
                        if (refreshDeviceProfiles) {
                          await refreshDeviceProfiles()
                        }
                        onModalClose()
                      } catch (error) {
                        console.error("Error during import:", error)
                        // Don't close modal on error so user can try again
                      }
                    }}
                  >
                    Import Preset
                  </Button>
                  <Button className="-mr-5" theme="none" size="sm" onPress={onModalClose}>
                    Cancel
                  </Button>
                </>
              )}
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

// Preview component for displaying parsed EQ settings
const EQPreview = ({ settings, deviceHas12Bands }) => {
  if (!settings) return null

  const filterOrder = deviceHas12Bands
    ? [
        "Lowshelf 1",
        "Lowshelf 2",
        "Peaking 1",
        "Peaking 2",
        "Peaking 3",
        "Peaking 4",
        "Peaking 5",
        "Peaking 6",
        "Peaking 7",
        "Peaking 8",
        "Highshelf 1",
        "Highshelf 2",
      ]
    : [
        "Lowshelf",
        "Peaking 1",
        "Peaking 2",
        "Peaking 3",
        "Peaking 4",
        "Peaking 5",
        "Peaking 6",
        "Peaking 7",
        "Peaking 8",
        "Highshelf",
      ]

  const enabledFilters = filterOrder.filter(
    (key) => settings[key] && settings[key].Gain?.Current !== 0
  )

  const totalEnabled = enabledFilters.length

  return (
    <div className="mt-4 rounded-plus border border-dark-green bg-green bg-opacity-10 p-3">
      <div className="mb-3 flex items-center justify-between">
        <div className="type-body-sm font-bold text-white">Imported Data Mapping Preview</div>
        <div className="type-body-xs text-silver">
          {totalEnabled} enabled filter{totalEnabled !== 1 ? "s" : ""}
        </div>
      </div>

      <div className="type-body-xs mb-3 italic text-ghost">
        Please verify the parsed data below matches your intended settings!
      </div>

      {settings.Preamp && (
        <div className="type-body-xs mb-3 text-ghost">
          Preamp:{" "}
          <span className="text-white">{settings.Preamp.Gain?.Current?.toFixed(1) || 0} dB</span>
        </div>
      )}

      {enabledFilters.length > 0 && (
        <div className="max-h-32 space-y-1 overflow-y-auto pr-2">
          {enabledFilters.map((key, index) => {
            const filter = settings[key]
            const gain = filter.Gain?.Current || 0
            const freq = filter.Frequency?.Current || 0
            const q = filter.Q?.Current || 0.707
            const type =
              filter.Type?.Current ||
              (key.includes("Lowshelf")
                ? "LOWSHELF"
                : key.includes("Highshelf")
                  ? "HIGHSHELF"
                  : "PEAKING")

            // Find the band number by looking up the key in the full filter order
            const bandNumber = filterOrder.indexOf(key) + 1

            // Map filter types to proper abbreviations
            const typeAbbrev = type === "LOWSHELF" ? "LS" : type === "HIGHSHELF" ? "HS" : "PK"

            return (
              <div key={key} className="type-body-xs flex items-center py-1">
                <span className="mr-3 font-medium text-ghost">Band {bandNumber}:</span>
                <span className="font-mono text-white">
                  {typeAbbrev} {freq}Hz {gain > 0 ? "+" : ""}
                  {gain.toFixed(1)}dB Q{q.toFixed(2)}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {enabledFilters.length === 0 && (
        <div className="type-body-xs py-4 text-center text-ghost">
          All filters disabled (0 dB gain)
        </div>
      )}
    </div>
  )
}

export default ImportPresetModal
